import { useParams, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useSubmitQuoteComment, useRejectQuote } from '@/hooks/useApi';

export default function QuoteAcceptPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'loading' | 'accepted' | 'rejected' | 'messageSent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [comment, setComment] = useState('');

  const submitComment = useSubmitQuoteComment();
  const rejectQuote = useRejectQuote();

  // Set language from URL ?lang= param (sent from email)
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang && ['en', 'fr', 'nl', 'es'].includes(lang)) {
      i18n.changeLanguage(lang);
    }
  }, [searchParams, i18n]);

  async function handleAccept() {
    setStatus('loading');
    try {
      await api.post(`/quotes/${id}/accept`);
      setStatus('accepted');
    } catch (err: unknown) {
      setStatus('error');
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErrorMsg(msg || t('quotes.acceptPage.errorDefault'));
    }
  }

  async function handleReject() {
    setStatus('loading');
    try {
      await rejectQuote.mutateAsync(id!);
      setStatus('rejected');
    } catch (err: unknown) {
      setStatus('error');
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErrorMsg(msg || t('quotes.acceptPage.errorDefault'));
    }
  }

  async function handleSendComment() {
    if (!comment.trim()) return;
    setStatus('loading');
    try {
      await submitComment.mutateAsync({ id: id!, message: comment.trim() });
      setStatus('messageSent');
    } catch (err: unknown) {
      setStatus('error');
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErrorMsg(msg || t('quotes.acceptPage.errorDefault'));
    }
  }

  const isLoading = status === 'loading';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {status === 'accepted' ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('quotes.acceptPage.successTitle')}</h1>
            <p className="text-gray-500">{t('quotes.acceptPage.successMessage')}</p>
          </div>
        ) : status === 'rejected' ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('quotes.acceptPage.rejectedTitle')}</h1>
            <p className="text-gray-500">{t('quotes.acceptPage.rejectedMessage')}</p>
          </div>
        ) : status === 'messageSent' ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('quotes.acceptPage.messageSentTitle')}</h1>
            <p className="text-gray-500">{t('quotes.acceptPage.messageSentMessage')}</p>
          </div>
        ) : status === 'error' ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('quotes.acceptPage.errorTitle')}</h1>
            <p className="text-gray-500">{errorMsg}</p>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-8 py-6">
              <h1 className="text-xl font-semibold text-white">{t('quotes.acceptPage.title')}</h1>
              <p className="text-indigo-200 text-sm mt-1">{t('quotes.acceptPage.subtitle')}</p>
            </div>
            <div className="p-8 space-y-6">
              <p className="text-gray-600">
                {t('quotes.acceptPage.body')}
              </p>

              {/* Comment / question section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('quotes.acceptPage.commentLabel')}
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  rows={3}
                  maxLength={2000}
                  placeholder={t('quotes.acceptPage.commentPlaceholder')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={isLoading}
                />
                {comment.trim() && (
                  <button
                    className="mt-2 w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-sm"
                    onClick={handleSendComment}
                    disabled={isLoading}
                  >
                    {t('quotes.acceptPage.sendComment')}
                  </button>
                )}
              </div>

              <div className="border-t pt-4 space-y-3">
                <button
                  className="w-full py-3 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                  onClick={handleAccept}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t('quotes.acceptPage.processing')}
                    </span>
                  ) : (
                    '✓ ' + t('quotes.acceptPage.button')
                  )}
                </button>
                <button
                  className="w-full py-3 px-6 bg-white border border-red-300 hover:bg-red-50 text-red-600 font-semibold rounded-lg transition-colors disabled:opacity-50"
                  onClick={handleReject}
                  disabled={isLoading}
                >
                  {t('quotes.acceptPage.rejectButton')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
